import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  FROM_DATE_TYPE_TRANSLATIONS,
  FROM_DATE_TYPES,
  type FromDateType,
  type SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';

interface ParityDropdownsFromDateTypeSelectionSignature {
  Args: {
    onChange: (fromDateType: FromDateType) => void;
    selectedFromDateType: FromDateType;
  };
  Element: HTMLDivElement;
}

export default class ParityDropdownsFromDateTypeSelection extends Component<ParityDropdownsFromDateTypeSelectionSignature> {
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];

  labelId = `from-date-type-selection-label-${guidFor(this)}`;
  triggerId = `from-date-type-selection-trigger-${guidFor(this)}`;

  @cached // Preserve reference for isEqual check in PowerSelect code
  get availableFromDateTypes(): SelectInputOptionWithId<FromDateType>[] {
    return Object.values(FROM_DATE_TYPES).map((fromDateType) => ({
      id: fromDateType,
      label: this.intl.t(FROM_DATE_TYPE_TRANSLATIONS[fromDateType]),
    }));
  }

  get isVisible(): boolean {
    return this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.PARITY_DYNAMIC_SHOPPING,
    );
  }

  get selectedFromDateType():
    | SelectInputOptionWithId<FromDateType>
    | undefined {
    return this.availableFromDateTypes.find(
      (fromDateType) => fromDateType.id === this.args.selectedFromDateType,
    );
  }

  @action fromDateTypeChanged(
    option: SelectInputOptionWithId<FromDateType>,
  ): void {
    this.args.onChange(option.id);
  }

  <template>
    {{#if this.isVisible}}
      <div
        class="flex shrink-0 items-center gap-200"
        data-test-id="from-date-type-selection"
        ...attributes
      >
        <span class="text-300--regular whitespace-nowrap" id={{this.labelId}}>
          {{t "general.arrival_date"}}
        </span>
        <PrismSelectInput
          @onChange={{this.fromDateTypeChanged}}
          @options={{this.availableFromDateTypes}}
          @value={{this.selectedFromDateType}}
          aria-labelledby="{{this.labelId}} {{this.triggerId}}"
          id={{this.triggerId}}
        />
      </div>
    {{/if}}
  </template>
}
